<!-- eslint-disable vue/no-v-html -->
<template>
    <div>
        <Typography
            is="div"
            ref="seoRef"
            type="paragraph"
            size="xs"
            :responsive="false"
            :class="[$style.seo, { [$style.seo_expanded]: seoExpanded }, $style[props.color]]"
        >
            <!-- key here helps top re-mount this element and fix glitch on Safari -->
            <div
                :key="seoExpanded ? 'expanded' : 'collapsed'"
                data-allow-mismatch
                v-html="content"
            />
        </Typography>
        <Button
            v-if="!seoExpanded"
            :class="$style.seo_button"
            color="carbon-400"
            interfaceTarget="expand-description"
            @click="onExpandTextClick"
        >
            {{ $tr('seo.show_full') }}
        </Button>
    </div>
</template>
<script lang="ts" setup>
import Typography from '@/components_new/Typography.vue'
import Button from '@/components_new/Button.vue'
import { onMounted, ref, computed } from 'vue'

const props = withDefaults(
    defineProps<{ content: string; minLinesToShow?: number; color?: 'white-80' | 'white-60' }>(),
    { minLinesToShow: 5, color: 'white-60' },
)

const content = computed(() => {
    let result = props.content

    /**
     * The props.content can be an invalid html string.
     * If that happens, the result SSR can be invalid as well, leading to broken layout on the client.
     * However, if this component is mounted without hydration, it will be fine, as on the client vue passes v-html content though DOMParser which autofixes all issues.
     * So the only problem is to do the same "fix" on the server side.
     * For that I'm using cheerio parsing/serialization.
     */
    if (typeof window === 'undefined') {
        /**
         * require() happens only on server
         * "cheerio" is added to webpack "externals" to prevent bundling
         */
        const loadOptions = null
        const isDocument = false // we are parsing "fragment"
        // eslint-disable-next-line @typescript-eslint/no-require-imports
        result = require('cheerio').load(result, loadOptions, isDocument).html()
    }

    // Process the content: remove HTML comments and add lazy loading
    return (
        result
            // Remove HTML comments
            .replace(/<!--[\s\S]*?-->/g, '')
            // Add loading="lazy" to img tag
            .replace(/(<img\s)([^>]*?)(\/?>)/gi, (match, start, attributes, end) => {
                if (attributes.includes('loading=')) {
                    return match
                }
                return `${start}loading="lazy" ${attributes}${end}`
            })
            // Add loading="lazy" to iframe tag
            .replace(/(<iframe\s)([^>]*?)(>)/gi, (match, start, attributes, end) => {
                if (attributes.includes('loading=')) {
                    return match
                }
                return `${start}loading="lazy" ${attributes}${end}`
            })
    )
})
const seoRef = ref<InstanceType<typeof Typography> | null>(null)
const seoExpanded = ref(false)
const defaultLineHeight = 16

function onExpandTextClick() {
    seoExpanded.value = true
}

onMounted(() => {
    if (!seoRef.value?.$el) {
        return
    }
    const seoClientHeight = seoRef.value.$el.clientHeight
    const seoScrollHeight = seoRef.value.$el.scrollHeight
    let seoLineHeight = parseInt(window.getComputedStyle(seoRef.value.$el).lineHeight, 10)
    if (Number.isNaN(seoLineHeight)) {
        seoLineHeight = defaultLineHeight
    }
    const diff = seoScrollHeight - seoClientHeight
    if (diff / seoLineHeight > props.minLinesToShow) {
        seoExpanded.value = false
    } else {
        seoExpanded.value = true
    }
})
</script>
<style module>
.seo {
    --color: var(--white-60);
    color: var(--color);
    height: 80px;
    overflow: hidden;
    -webkit-mask-image: -webkit-linear-gradient(to bottom, black 55%, transparent 95%);
    mask-image: linear-gradient(to bottom, black 55%, transparent 95%);
}

.seo.white-60 {
    --color: var(--white-60);
}

.seo.white-80 {
    --color: var(--white-80);
}

.seo h2,
.seo h3,
.seo h4,
.seo h5,
.seo h6 {
    margin-top: 16px;
    margin-bottom: 8px;
    font-size: var(--font-size-label-l);
    font-weight: var(--font-weight-accent);
    line-height: var(--line-height-label);
}

.seo h2:first-child,
.seo h3:first-child,
.seo h4:first-child,
.seo h5:first-child,
.seo h6:first-child {
    margin-top: 0;
}

.seo ul {
    list-style-type: disc;
    list-style-position: inside;
}

.seo_button {
    margin-top: 8px;
}

@media (--mobile) {
    .seo {
        height: 120px;
    }
}

.seo_expanded {
    height: auto;
    overflow: visible;
    -webkit-mask-image: unset;
    mask-image: unset;
}
</style>
